import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { androidFailureHint } from './AndroidPackagePanel';
import CustomDomainPanel from './CustomDomainPanel';
import IosAppPanel from './IosAppPanel';
import './AndroidPackagePanel.css';
import './MyAppButton.css';

// "My app" (2026-10-16): one compact button in the chat for everything after the Preview -
// installing on a phone, publishing, addresses, stores and managing what was published.
// Every real action goes through an existing flow with its own approval: building the Android
// app (credits shown first), the chat deployment flow, paid addresses through Stripe, and
// changes to the app through the agent (Builder -> Sandbox -> Preview). Nothing here shows
// success before the server verified it.

export const PUBLISH_UPDATE_TEXT = 'פרסם את האתר לחשבון שלי';
const POLL_MS = 8000;

export function androidHeadline(android) {
  if (!android) return '';
  if (android.state === 'QUEUED' || android.state === 'RUNNING') return 'מכינים את האפליקציה לאנדרואיד... (כ-3 דקות)';
  if (android.state === 'FAILED') return androidFailureHint(android.errorCode);
  if (android.downloadable && android.needsNewVersion) return 'שינית את האפליקציה מאז הגרסה האחרונה. כדי לראות את השינויים בטלפון צריך גרסה חדשה.';
  if (android.downloadable && android.updateForDevice) return 'יש עדכון לאפליקציה שלך — עדכן במכשירך';
  if (android.downloadable) return 'האפליקציה שלך מוכנה! הורד את האפליקציה למכשירך';
  return 'הפוך את האתר לאפליקציית אנדרואיד אמיתית.';
}

// What the customer pays for when building the Android app - shown before the first build, so it
// is clear this is a real app and not another link to the website (that link already exists).
export function androidValuePoints() {
  return [
    'אפליקציה שמותקנת בטלפון עם אייקון משלה במסך הבית — לא עוד קישור לאתר.',
    'נפתחת במסך מלא, כמו כל אפליקציה, בלי שורת כתובת של דפדפן.',
    'כל קבצי האתר בתוך האפליקציה, כך שהיא ממשיכה לעבוד גם אחרי שקישור התצוגה הזמני פג.',
    'חתומה במפתח קבוע של האפליקציה שלך, כך שכל גרסה חדשה מתעדכנת מעל הקודמת.',
    'הבסיס לפרסום עתידי ב-Google Play (הפרסום בחנות עצמו הוא שלב נפרד).'
  ];
}

export function androidPriceText(cost) {
  if (!(cost > 0)) return '';
  return `המחיר: ${cost} קרדיטים לכל גרסה — כולל בנייה, חתימה ובדיקה. הורדה חוזרת של אותה גרסה בחינם, ואם הבנייה נכשלת הקרדיטים חוזרים אליך.`;
}

export function androidInstallSteps(isUpdate) {
  return isUpdate
    ? ['פתחו את הקובץ שהורד בטלפון.', 'לחצו "עדכן". האפליקציה תתעדכן בלי למחוק אותה.']
    : ['פתחו את הקובץ שהורד (בהתראות למעלה או בתיקיית ההורדות).', 'אם מופיעה אזהרה, אשרו את ההורדה ואת ההתקנה ממקור זה.', 'לחצו "התקן". האפליקציה תופיע במסך הבית.'];
}

export function iphoneSteps() {
  return ['פתחו את הקישור ב-Safari באייפון.', 'לחצו על כפתור השיתוף (ריבוע עם חץ למעלה).', 'בחרו "הוסף למסך הבית" ואשרו.'];
}

export const DOMAIN_STATUS_TEXT = {
  NOT_FOUND: 'הדומיין עדיין לא מוגדר. בצעו את השלבים למטה ובדקו שוב.',
  POINTS_ELSEWHERE: 'הדומיין מפנה כרגע למקום אחר. עדכנו את ההגדרה לפי השלבים למטה.',
  CONNECTING: 'הדומיין מפנה לאפליקציה. מחכים שהחיבור המאובטח (https) יופעל — זה יכול לקחת עד כמה שעות.',
  ACTIVE: 'הדומיין מחובר ועובד.'
};

const STORE_LABEL = { google_play: 'Google Play', app_store: 'App Store' };
const ITEM_ICON = { ok: '✓', missing: '✗', customer_action: '👤', warning: '!', not_available: '⏳' };

export function formatDate(value) {
  try { return new Date(value).toLocaleDateString('he-IL', { day: 'numeric', month: 'long' }); } catch { return ''; }
}

function LiveDot() {
  return <span className="package-live-dot" aria-hidden="true"><span className="package-live-dot-core" /><span className="package-live-dot-ring" /></span>;
}

function errorText(err, fallback) {
  return err?.response?.data?.error || fallback;
}

// Phones only start a download from the customer's own tap on a real link. Starting it from a
// script after waiting for the server (the previous approach) is silently blocked on Android,
// especially inside the home-screen app. So the link is prepared in advance (free, not counted
// as a download), refreshed before it expires, and shown as a normal link to tap.
const DOWNLOAD_REFRESH_MS = 8 * 60 * 1000;

function AndroidSection({ projectId, android, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const [file, setFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const downloadable = Boolean(android?.downloadable);
  const versionCode = android?.versionCode;

  useEffect(() => {
    if (!downloadable) { setFile(null); return undefined; }
    let cancelled = false;
    let timer = null;
    const prepare = async () => {
      try {
        const data = await api.androidDownload(projectId, false);
        if (!cancelled) { setFile(data.download); setError(''); }
      } catch (err) {
        if (!cancelled) setError(errorText(err, 'לא הצלחנו להכין את ההורדה. נסו שוב בעוד רגע.'));
      }
      if (!cancelled) timer = setTimeout(prepare, DOWNLOAD_REFRESH_MS);
    };
    prepare();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [projectId, downloadable, versionCode]);

  if (!android) return null;
  const running = android.state === 'QUEUED' || android.state === 'RUNNING';
  const canBuild = !running && (android.available !== false);
  const needsBuild = !android.downloadable || android.needsNewVersion;
  const isUpdate = Boolean(android.updateForDevice);

  const build = async () => {
    setBusy(true); setError('');
    try { await api.requestAndroidPackage(projectId); await onChanged(); }
    catch (err) { setError(errorText(err, 'לא ניתן להתחיל כרגע. נסו שוב בעוד רגע.')); }
    finally { setBusy(false); }
  };
  // The tap itself downloads (normal link); this only records it, in the background.
  const recordDownload = () => {
    setDownloaded(true);
    api.androidDownload(projectId, true).then(() => onChanged()).catch(() => {});
  };
  const copyLink = () => {
    if (!file?.url || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(file.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };

  return (
    <section className="my-app-section" aria-live="polite">
      <h4>אנדרואיד</h4>
      <p className="my-app-status">{running && <LiveDot />}{androidHeadline(android)}</p>
      {android.downloadable && <small className="my-app-muted">גרסה {android.versionName}</small>}
      {!android.downloadable && !running && (
        <ul className="my-app-value">{androidValuePoints().map(point => <li key={point}>{point}</li>)}</ul>
      )}
      <div className="my-app-actions">
        {android.downloadable && (file?.url
          ? <a className="btn submit-btn my-app-download" href={file.url} download={file.fileName} rel="noopener noreferrer" onClick={recordDownload}>{isUpdate ? 'הורד את העדכון' : 'הורד את האפליקציה'}</a>
          : <button type="button" className="btn submit-btn" disabled>מכין הורדה...</button>)}
        {canBuild && needsBuild && (
          <button type="button" className={`btn ${android.downloadable ? '' : 'submit-btn'}`} disabled={busy} onClick={build}>
            {android.downloadable ? 'צור גרסה חדשה' : android.state === 'FAILED' ? 'נסה שוב' : 'הפוך לאפליקציית אנדרואיד'}
            {android.buildCreditCost > 0 ? ` (${android.buildCreditCost} קרדיטים)` : ''}
          </button>
        )}
      </div>
      {android.blockedReason === 'FULL_STACK_PUBLISH_FIRST' && (
        <p className="my-app-status">לאפליקציה הזו יש שרת ומסד נתונים. כדי שאפליקציית האנדרואיד תעבוד עם הנתונים גם אחרי ההתקנה, צריך קודם לפרסם את האפליקציה (״פרסם את האפליקציה״ בתפריט). אחרי הפרסום יופיע כאן הכפתור ליצירת אפליקציית אנדרואיד.</p>
      )}
      {canBuild && needsBuild && android.buildCreditCost > 0 && <small className="my-app-muted">{androidPriceText(android.buildCreditCost)}</small>}
      {downloaded && <p className="my-app-status is-ok">ההורדה התחילה. הקובץ יופיע בהתראות בראש המסך.</p>}
      {android.downloadable && (
        <ol className="my-app-steps">{androidInstallSteps(isUpdate).map(step => <li key={step}>{step}</li>)}</ol>
      )}
      {android.downloadable && file?.url && (
        <small className="my-app-muted">
          ההורדה לא התחילה? <button type="button" className="my-app-inline-btn" onClick={copyLink}>{copied ? '✓ הקישור הועתק' : 'העתיקו את הקישור'}</button> והדביקו אותו בדפדפן Chrome. הקישור מתחדש אוטומטית.
        </small>
      )}
      {android.downloadable && <small className="my-app-muted">האפליקציה המותקנת ממשיכה לעבוד גם אחרי שקישור ההורדה פג. שינויים באפליקציה מגיעים לטלפון רק דרך גרסה חדשה.</small>}
      {error && <p className="my-app-error" role="alert">{error}</p>}
    </section>
  );
}

function IphoneSection({ projectId, iphone, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  if (!iphone) return null;
  const link = iphone.permanentUrl || iphone.testLink?.url || null;
  const create = async () => {
    setBusy(true); setError('');
    try { await api.iphoneTestLink(projectId); await onChanged(); }
    catch (err) { setError(errorText(err, 'לא ניתן ליצור קישור כרגע.')); }
    finally { setBusy(false); }
  };
  const copy = () => {
    if (!link || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  };
  return (
    <section className="my-app-section">
      <h4>אייפון</h4>
      {iphone.permanentUrl
        ? <p className="my-app-status">האפליקציה פורסמה. הוסיפו את הכתובת הקבועה למסך הבית — היא תמיד תציג את הגרסה שפרסמת.</p>
        : iphone.testLink?.url
          ? <p className="my-app-status">קישור בדיקה פעיל עד {formatDate(iphone.testLink.expiresAt)}. הוא מציג תמיד את הגרסה האחרונה שבנית.</p>
          : <p className="my-app-status">{iphone.testLink?.expired ? 'קישור הבדיקה הקודם פג.' : 'אפשר לבדוק את האפליקציה באייפון דרך קישור זמני, ולהוסיף אותה למסך הבית.'}</p>}
      {link && (
        <>
          <a className="my-app-link" href={link} target="_blank" rel="noopener noreferrer">{link}</a>
          <ol className="my-app-steps">{iphoneSteps().map(step => <li key={step}>{step}</li>)}</ol>
        </>
      )}
      <div className="my-app-actions">
        {link && <button type="button" className="btn" onClick={copy}>{copied ? '✓ הועתק' : 'העתק קישור'}</button>}
        {!iphone.permanentUrl && <button type="button" className={`btn ${link ? '' : 'submit-btn'}`} disabled={busy} onClick={create}>{iphone.testLink?.url ? 'צור קישור חדש' : 'צור קישור לבדיקה'}</button>}
      </div>
      <small className="my-app-muted">{iphone.note}{!iphone.permanentUrl ? ' קישור הבדיקה זמני; לשימוש קבוע פרסמו את האפליקציה.' : ''}</small>
      {error && <p className="my-app-error" role="alert">{error}</p>}
    </section>
  );
}

function DomainCheck({ projectId, initial }) {
  const [domain, setDomain] = useState(initial?.domain || '');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const check = async () => {
    setBusy(true); setError('');
    try { const data = await api.domainCheck(projectId, domain); setResult(data.domain); }
    catch (err) { setError(errorText(err, 'הבדיקה לא הצליחה. נסו שוב.')); }
    finally { setBusy(false); }
  };
  const status = result?.status || initial?.status || null;
  return (
    <div className="my-app-action-card">
      <label className="my-app-field">
        <span>הדומיין שלכם</span>
        <input dir="ltr" value={domain} placeholder="myshop.com" onChange={event => setDomain(event.target.value)} />
      </label>
      <button type="button" className="btn submit-btn" disabled={busy || !domain.trim()} onClick={check}>{busy ? 'בודק...' : 'בדוק חיבור'}</button>
      {status && <p className={`my-app-status ${status === 'ACTIVE' ? 'is-ok' : ''}`}>{status === 'CONNECTING' && <LiveDot />}{DOMAIN_STATUS_TEXT[status]}</p>}
      {result?.instructions && status !== 'ACTIVE' && <ol className="my-app-steps">{result.instructions.map(line => <li key={line}>{line.replace(/^\d+\.\s*/, '')}</li>)}</ol>}
      {error && <p className="my-app-error" role="alert">{error}</p>}
    </div>
  );
}

function StoreReadiness({ projectId, store, onAskAgent }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = useCallback(async () => {
    setBusy(true); setError('');
    try { const data = await api.storeReadiness(projectId, store); setResult(data.readiness); }
    catch (err) { setError(errorText(err, 'הבדיקה לא הצליחה. נסו שוב.')); }
    finally { setBusy(false); }
  }, [projectId, store]);
  return (
    <div className="my-app-action-card">
      <strong>{STORE_LABEL[store]}</strong>
      {!result && <button type="button" className="btn submit-btn" disabled={busy} onClick={run}>{busy ? 'בודק...' : 'בדוק אם האפליקציה מוכנה'}</button>}
      {result && (
        <>
          <p className={`my-app-status ${result.ready ? 'is-ok' : ''}`}>{result.ready ? 'האפליקציה מוכנה להגשה.' : 'כמה דברים חסרים לפני שאפשר להגיש:'}</p>
          <ul className="my-app-checklist">
            {result.items.map(item => <li key={item.id} className={`is-${item.status}`}><span aria-hidden="true">{ITEM_ICON[item.status] || '•'}</span> {item.text}</li>)}
          </ul>
          {result.agentFixRequest && <button type="button" className="btn submit-btn" onClick={() => onAskAgent(`השלם בבקשה את הדברים הבאים כדי שהאפליקציה תהיה מוכנה ל-${STORE_LABEL[store]}:\n${result.agentFixRequest}`)}>בקש מהסוכן להשלים</button>}
          {store === 'app_store' && result.items.some(item => item.id === 'ios-build' && item.status === 'ok') && <IosAppPanel projectId={projectId} />}
          <small className="my-app-muted">{result.disclaimer}</small>
          <button type="button" className="btn" disabled={busy} onClick={run}>בדוק שוב</button>
        </>
      )}
      {error && <p className="my-app-error" role="alert">{error}</p>}
    </div>
  );
}

function JourneyAction({ action, projectId, myApp, onChat, onClose }) {
  if (action.type === 'chat') {
    return (
      <div className="my-app-action-card">
        <p>{action.text}</p>
        <button type="button" className="btn submit-btn" onClick={() => { onChat(action.chatMessage); onClose(); }}>המשך לחיבור</button>
      </div>
    );
  }
  if (action.type === 'status') {
    return <div className="my-app-action-card"><p className={`my-app-status ${action.status === 'PUBLISHED' ? 'is-ok' : ''}`}>{action.status === 'CHECKING_DEPLOYMENT' && <LiveDot />}{action.text}</p></div>;
  }
  if (action.type === 'domain_check') return <><p className="my-app-muted">{action.text}</p><DomainCheck projectId={projectId} initial={myApp.published?.domainConnection} /></>;
  if (action.type === 'subdomain') return <div className="my-app-action-card"><p>{action.text}</p><CustomDomainPanel /></div>;
  if (action.type === 'buy_domain') {
    return (
      <div className="my-app-action-card">
        <p>{action.text}</p>
        {action.registrarUrl && <a className="btn" href={action.registrarUrl} target="_blank" rel="noopener noreferrer">מעבר לקניית דומיין</a>}
      </div>
    );
  }
  if (action.type === 'store_readiness') {
    return <>{action.stores.map(store => <StoreReadiness key={store} projectId={projectId} store={store} onAskAgent={text => { onChat(text); onClose(); }} />)}</>;
  }
  return null;
}

function PublishGuide({ projectId, myApp, onJourney, onChat, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const journey = myApp.journey || {};
  const send = async payload => {
    setBusy(true); setError('');
    try { await onJourney(payload); }
    catch (err) { setError(errorText(err, 'משהו השתנה בינתיים. פתחו שוב את "האפליקציה שלי".')); }
    finally { setBusy(false); }
  };
  const hasAnswers = Object.keys(journey.answers || {}).length > 0;
  const actions = journey.actions || (journey.action ? [journey.action] : []);
  return (
    <section className="my-app-section">
      {journey.recommendedChoice && <p className="my-app-muted">המלצנו עבורך על האפשרות המתאימה ביותר. אפשר לחזור ולשנות.</p>}
      {journey.question && (
        <>
          <h4>{journey.question.text}</h4>
          <div className="my-app-options">
            {journey.question.options.map(option => (
              <button key={option.id} type="button" className={`my-app-option ${option.recommended ? 'is-recommended' : ''}`} disabled={busy || option.disabled} onClick={() => send({ questionId: journey.question.id, optionId: option.id })}>
                <span className="my-app-option-label">{option.label}{option.recommended && option.id !== 'recommend' ? ' (מומלץ)' : ''}</span>
                {option.description && <span className="my-app-option-desc">{option.description}</span>}
              </button>
            ))}
          </div>
          {journey.question.help && <small className="my-app-muted">{journey.question.help}</small>}
        </>
      )}
      {actions.map((action, index) => <JourneyAction key={`${action.type}-${index}`} action={action} projectId={projectId} myApp={myApp} onChat={onChat} onClose={onClose} />)}
      {hasAnswers && (
        <div className="my-app-actions">
          <button type="button" className="btn" disabled={busy} onClick={() => send({ back: true })}>חזרה לשאלה הקודמת</button>
          <button type="button" className="btn" disabled={busy} onClick={() => send({ questionId: 'reset', optionId: 'reset' })}>התחל מחדש</button>
        </div>
      )}
      <small className="my-app-muted">אפשר לסגור ולהמשיך לפתח בכל רגע — נחזור לאותה נקודה. לפני כל תשלום יוצגו המחיר, התקופה ומה כלול.</small>
      {error && <p className="my-app-error" role="alert">{error}</p>}
    </section>
  );
}

function ManageView({ myApp, onChat, onClose, openPublish }) {
  const published = myApp.published || {};
  const stores = Object.entries(published.storeReadiness || {});
  return (
    <section className="my-app-section">
      <h4>האפליקציה שפרסמת</h4>
      <p className="my-app-status is-ok">פועלת בכתובת:</p>
      <a className="my-app-link" href={published.url} target="_blank" rel="noopener noreferrer">{published.url}</a>
      {published.unpublishedChanges
        ? (
          <div className="my-app-action-card">
            <p>יש שינויים שבנית ועדיין לא פורסמו. הגרסה הפומבית לא משתנה עד שתאשר.</p>
            <button type="button" className="btn submit-btn" onClick={() => { onChat(PUBLISH_UPDATE_TEXT); onClose(); }}>פרסם עדכון</button>
          </div>
        )
        : <p className="my-app-muted">הגרסה שפורסמה זהה לגרסה האחרונה שבנית.</p>}
      {published.domainConnection && <p className="my-app-muted">דומיין {published.domainConnection.domain}: {DOMAIN_STATUS_TEXT[published.domainConnection.status]}</p>}
      {myApp.android?.versionName && myApp.android?.downloadable && <p className="my-app-muted">גרסת אנדרואיד אחרונה: {myApp.android.versionName}</p>}
      {stores.map(([store, info]) => <p key={store} className="my-app-muted">{STORE_LABEL[store] || store}: {info.ready ? 'מוכנה להגשה' : `חסרים ${info.missing?.length || 0} פריטים`} (נבדק {formatDate(info.checkedAt)})</p>)}
      <button type="button" className="btn" onClick={openPublish}>כתובת, דומיין או חנויות</button>
    </section>
  );
}

export default function MyAppButton({ projectId, disabled, onChat, openSignal = 0 }) {
  const [view, setView] = useState(null);
  const [myApp, setMyApp] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId) return null;
    try {
      const data = await api.myApp(projectId);
      setMyApp(data.myApp);
      setError('');
      return data.myApp;
    } catch (err) {
      setError(errorText(err, 'לא ניתן לטעון כרגע. נסו שוב.'));
      return null;
    }
  }, [projectId]);

  useEffect(() => { if (view) load(); }, [view, load]);

  // Opened from the side menu ("האפליקציות שלי").
  useEffect(() => {
    if (!openSignal) return;
    setView('menu');
    setTimeout(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  }, [openSignal]);

  useEffect(() => {
    clearTimeout(timer.current);
    const pending = myApp && (['QUEUED', 'RUNNING'].includes(myApp.android?.state) || myApp.journey?.action?.status === 'CHECKING_DEPLOYMENT');
    if (view && pending) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [myApp, view, load]);

  useEffect(() => {
    if (!view) return undefined;
    const onKey = event => { if (event.key === 'Escape') setView(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view]);

  if (!projectId) return null;
  const close = () => setView(null);
  const onJourney = async payload => {
    const data = await api.myAppJourney(projectId, payload);
    setMyApp(current => ({ ...(current || {}), journey: data.journey }));
  };
  const menu = myApp?.menu || [{ id: 'install', label: 'התקן או עדכן בטלפון' }, { id: 'publish', label: 'פרסם את האפליקציה' }];
  const title = { menu: 'האפליקציה שלי', install: 'התקן או עדכן בטלפון', publish: 'פרסם את האפליקציה', manage: 'נהל את האפליקציה שפרסמתי' }[view];

  return (
    <div className="my-app" dir="rtl" ref={rootRef}>
      <button type="button" className="my-app-pill" aria-expanded={Boolean(view)} disabled={disabled} onClick={() => setView(view ? null : 'menu')}>
        <span aria-hidden="true">📱</span> האפליקציה שלי
      </button>
      {view && (
        <div className="my-app-panel" role="dialog" aria-label={title}>
          <div className="my-app-head">
            {view !== 'menu' && <button type="button" className="my-app-icon-btn" aria-label="חזרה" onClick={() => setView('menu')}>→</button>}
            <strong>{title}</strong>
            <button type="button" className="my-app-icon-btn" aria-label="סגור" onClick={close}>✕</button>
          </div>
          {error && <p className="my-app-error" role="alert">{error}</p>}
          {!myApp && !error && <p className="my-app-muted"><LiveDot /> טוען...</p>}
          {myApp && view === 'menu' && (
            <div className="my-app-menu">
              {menu.map(item => <button key={item.id} type="button" className="my-app-option" onClick={() => setView(item.id)}><span className="my-app-option-label">{item.label}</span></button>)}
              {myApp.published?.live && <small className="my-app-muted">פורסם: {myApp.published.url}</small>}
            </div>
          )}
          {myApp && view === 'install' && (
            <>
              <AndroidSection projectId={projectId} android={myApp.android} onChanged={load} />
              <IphoneSection projectId={projectId} iphone={myApp.iphone} onChanged={load} />
            </>
          )}
          {myApp && view === 'publish' && <PublishGuide projectId={projectId} myApp={myApp} onJourney={onJourney} onChat={onChat} onClose={close} />}
          {myApp && view === 'manage' && myApp.published?.live && <ManageView myApp={myApp} onChat={onChat} onClose={close} openPublish={() => setView('publish')} />}
        </div>
      )}
    </div>
  );
}
